import { useEffect, useState } from 'react'

/**
 * One-time dismissed flag backed by localStorage. Returns `[seen, markSeen]`.
 *
 * SSR-safe: returns `seen = false` on the initial render (server and first
 * client paint) and reads the flag in a mount effect, so the UI can't flash
 * a mismatched state during hydration. Callers should default to the
 * "not-yet-seen" behavior (e.g. show the hint) and let the effect flip it.
 */
export function useOnceDismissed(key: string): [boolean, () => void] {
	const [seen, setSeen] = useState(false)

	useEffect(() => {
		try {
			if (window.localStorage.getItem(key) === '1') {
				setSeen(true)
			}
		} catch {
			// localStorage can throw in private browsing modes or when disabled
			// by policy. Fall back to always-unseen (show the hint every time).
		}
	}, [key])

	const markSeen = () => {
		setSeen(true)
		try {
			window.localStorage.setItem(key, '1')
		} catch {
			// ignore — in-memory state still carries for the current session
		}
	}

	return [seen, markSeen]
}
